import React, { useState } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useQuery } from "react-query";
import moment from "moment";
import useLang from "Shared/hooks/useLanguage";
import { sortById, SortingTypeI } from "Utils/consts";
import { Loader } from "Shared/Common/Loader";
import { ArrowDown, ArrowUp } from "Shared/Icons";
import usePagination from "Shared/hooks/usePagination";
import { BatchReagentsGetAll } from "Services/BatchReagents/BatchReagents";
import NoRecord from "Shared/Common/NoRecord";
import SearchDatePicker from "Shared/Common/DatePicker/SearchDatePicker";
import InactiveReagentsRow from "./row";

const initialSearchCriteria = {
  reagentsName: "",
  previousLotNumber: "",
  reagentLotNumber: "",
  startedDate: "",
  endDate: "",
  expirationDate: "",
};

const InactiveReagents: React.FC = () => {
  const { t } = useLang();
  const [triggerSearch, setTriggerSearch] = useState(false);
  const [sortTrigger, setSortTrigger] = useState(false);
  const [searchCriteria, setSearchCriteria] = useState(initialSearchCriteria);
  const [reset, setReset] = useState(false);
  const [sort, setSorting] = useState<SortingTypeI>(sortById);

  const {
    total,
    curPage,
    showPage,
    nextPage,
    prevPage,
    setTotal,
    pageSize,
    totalPages,
    pageNumbers,
    setPageSize,
    setCurPage,
  } = usePagination();

  const handleSort = (col: string) => {
    const newOrder =
      sort.clickedIconData === col
        ? sort.sortingOrder === "asc"
          ? "desc"
          : "asc"
        : "asc";
    setSorting({ sortingOrder: newOrder, clickedIconData: col });
    setSortTrigger((p) => !p);
  };

  const toISO = (d: string) =>
    d ? moment.utc(d, "MM/DD/YYYY").startOf("day").toISOString() : null;

  const queryPayload = {
    pageNumber: curPage,
    pageSize,
    queryModel: {
      reagentsName: searchCriteria.reagentsName || "",
      previousLotNumber: searchCriteria.previousLotNumber || "",
      reagentLotNumber: searchCriteria.reagentLotNumber || "",
      ...(searchCriteria.startedDate && { startedDate: toISO(searchCriteria.startedDate) }),
      ...(searchCriteria.endDate && { endDate: toISO(searchCriteria.endDate) }),
      ...(searchCriteria.expirationDate && { expirationDate: toISO(searchCriteria.expirationDate) }),
      isActive: false,
    },
    sortColumn: sort.clickedIconData || "Id",
    sortDirection: sort.sortingOrder || "Desc",
  };

  const {
    data: queryResult,
    isFetching: loading,
    refetch,
  } = useQuery(
    ["reagents-inactive", curPage, pageSize, triggerSearch, sortTrigger, reset],
    () => BatchReagentsGetAll(queryPayload),
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      onSuccess: (resp: any) => setTotal(resp?.data?.total ?? 0),
      onError: () => console.error("Error fetching inactive reagents data"),
    }
  );

  const apiRows: any[] = queryResult?.data?.data ?? [];

  const handleSearch = () => {
    setCurPage(1);
    setTriggerSearch((p) => !p);
  };

  const handleReset = () => {
    setSearchCriteria(initialSearchCriteria);
    setSorting(sortById);
    setCurPage(1);
    setReset((p) => !p);
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") handleSearch();
  };

  const sortIcons = (col: string) => ({
    up: `${
      sort.sortingOrder === "desc" && sort.clickedIconData === col
        ? "text-success fs-7"
        : "text-gray-700 fs-7"
    } p-0 m-0`,
    down: `${
      sort.sortingOrder === "asc" && sort.clickedIconData === col
        ? "text-success fs-7"
        : "text-gray-700 fs-7"
    } p-0 m-0`,
  });

  return (
    <>
      {/* Toolbar */}
      <div className="d-flex flex-wrap justify-content-center justify-content-sm-between align-items-center responsive-flexed-actions mb-2 gap-2">
        <div className="d-flex align-items-center">
          <span className="fw-400 mr-2">{t("Records")}</span>
          <select
            id="InactiveReagentsRecords"
            className="form-select w-100px h-33px rounded py-2"
            value={pageSize}
            onChange={(e) => {
              setPageSize(parseInt(e.target.value));
              setCurPage(1);
            }}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="50">50</option>
            <option value="100">100</option>
          </select>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button
            id="InactiveReagentsSearch"
            className="btn btn-linkedin btn-sm fw-500"
            onClick={handleSearch}
          >
            {t("Search")}
          </button>
          <button
            id="InactiveReagentsReset"
            className="btn btn-secondary btn-sm btn-secondary--icon fw-500"
            onClick={handleReset}
          >
            <span>{t("Reset")}</span>
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <Box sx={{ height: "auto", width: "100%" }}>
          <div className="table_bordered overflow-hidden">
            <TableContainer
              sx={{
                maxHeight: "calc(100vh - 100px)",
                "&::-webkit-scrollbar": { width: 7 },
                "&::-webkit-scrollbar-track": { backgroundColor: "#fff" },
                "&:hover": {
                  "&::-webkit-scrollbar-thumb": {
                    backgroundColor: "var(--kt-gray-400)",
                    borderRadius: 2,
                  },
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "var(--kt-gray-400)",
                  borderRadius: 2,
                },
              }}
              component={Paper}
              className="shadow-none"
            >
              <Table
                aria-label="inactive reagents table"
                className="table table-cutome-expend table-bordered table-sticky-header table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
              >
                <TableHead>
                  {/* Search row */}
                  <TableRow className="h-40px">
                    <TableCell className="w-50px" />
                    <TableCell className="w-50px" />
                    <TableCell style={{ minWidth: "180px" }}>
                      <input
                        type="text"
                        className="form-control bg-white min-w-150px w-100 rounded-2 fs-8 h-30px"
                        placeholder="Search..."
                        value={searchCriteria.reagentsName}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            reagentsName: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <input
                        type="text"
                        className="form-control bg-white min-w-130px w-100 rounded-2 fs-8 h-30px"
                        placeholder="Search..."
                        value={searchCriteria.previousLotNumber}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            previousLotNumber: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "150px" }}>
                      <input
                        type="text"
                        className="form-control bg-white min-w-120px w-100 rounded-2 fs-8 h-30px"
                        placeholder="Search..."
                        value={searchCriteria.reagentLotNumber}
                        onKeyDown={handleKeyPress}
                        onChange={(e) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            reagentLotNumber: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <SearchDatePicker
                        name="startedDate"
                        value={searchCriteria.startedDate}
                        onChange={(e: any) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            startedDate: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <SearchDatePicker
                        name="endDate"
                        value={searchCriteria.endDate}
                        onChange={(e: any) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            endDate: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <SearchDatePicker
                        name="expirationDate"
                        value={searchCriteria.expirationDate}
                        onChange={(e: any) =>
                          setSearchCriteria((p) => ({
                            ...p,
                            expirationDate: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell style={{ minWidth: "130px" }} />
                  </TableRow>

                  {/* Column headers */}
                  <TableRow className="h-30px">
                    <TableCell>{t("Actions")}</TableCell>
                    <TableCell>{t("Sr.No")}</TableCell>
                    <TableCell style={{ minWidth: "180px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("reagentsName")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Reagent Name")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp CustomeClass={sortIcons("reagentsName").up} />
                          <ArrowDown CustomeClass={sortIcons("reagentsName").down} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("previousLotNumber")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Lot Item Number")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp CustomeClass={sortIcons("previousLotNumber").up} />
                          <ArrowDown CustomeClass={sortIcons("previousLotNumber").down} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "150px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("reagentLotNumber")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Reagent's Lot #")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp CustomeClass={sortIcons("reagentLotNumber").up} />
                          <ArrowDown CustomeClass={sortIcons("reagentLotNumber").down} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("startedDate")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Started Date")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp CustomeClass={sortIcons("startedDate").up} />
                          <ArrowDown CustomeClass={sortIcons("startedDate").down} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("endDate")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Receive Date")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp CustomeClass={sortIcons("endDate").up} />
                          <ArrowDown CustomeClass={sortIcons("endDate").down} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "160px" }}>
                      <div
                        className="d-flex justify-content-between cursor-pointer"
                        onClick={() => handleSort("expirationDate")}
                      >
                        <span style={{ width: "max-content" }}>
                          {t("Expiration Date")}
                        </span>
                        <div className="d-flex align-items-center mx-4 mr-0">
                          <ArrowUp CustomeClass={sortIcons("expirationDate").up} />
                          <ArrowDown CustomeClass={sortIcons("expirationDate").down} />
                        </div>
                      </div>
                    </TableCell>
                    <TableCell style={{ minWidth: "130px" }}>
                      {t("Edit By")}
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={9}>
                        <Loader />
                      </TableCell>
                    </TableRow>
                  ) : apiRows.length ? (
                    apiRows.map((row: any, index: number) => (
                      <InactiveReagentsRow
                        key={row.batchReagentsId}
                        row={row}
                        serialNumber={(curPage - 1) * pageSize + index + 1}
                        refetch={refetch}
                      />
                    ))
                  ) : (
                    <NoRecord colSpan={9} />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </div>

      {/* Pagination */}
      <div className="d-flex flex-wrap gap-2 justify-content-center justify-content-sm-between align-items-center mt-4">
        <p className="pagination-total-record mb-0">
          <span>
            {t("Showing")} {pageSize * (curPage - 1) + 1} {t("to")}{" "}
            {Math.min(pageSize * curPage, total)} {t("of Total")}
            <span> {total} </span>
            {t("entries")}
          </span>
        </p>
        <ul className="d-flex align-items-center justify-content-end custome-pagination p-0 mb-0">
          <li className="btn btn-lg p-2 h-33px" onClick={() => showPage(1)}>
            <i className="fa fa-angle-double-left" />
          </li>
          <li className="btn btn-lg p-2 h-33px" onClick={prevPage}>
            <i className="fa fa-angle-left" />
          </li>
          {pageNumbers.map((page) => (
            <li
              key={page}
              className={`px-2 ${
                page === curPage
                  ? "font-weight-bold bg-primary text-white h-33px"
                  : ""
              }`}
              style={{ cursor: "pointer" }}
              onClick={() => showPage(page)}
            >
              {page}
            </li>
          ))}
          <li className="btn btn-lg p-2 h-33px" onClick={nextPage}>
            <i className="fa fa-angle-right" />
          </li>
          <li
            className="btn btn-lg p-2 h-33px"
            onClick={() => showPage(totalPages === 0 ? curPage : totalPages)}
          >
            <i className="fa fa-angle-double-right" />
          </li>
        </ul>
      </div>
    </>
  );
};

export default InactiveReagents;
